import { getEffectiveCableFuse } from "../cable-current";
import { bankPositiveVertices, computeBatteryStacks } from "../battery-stack";
import type { DefLookup } from "../catalog";
import { computeInstallationRuns } from "../installation-limits";
import { computeSystemCableSpecs } from "../cable-spec";
import { computeFridgeInstallationPolicies } from "../fridge-installation-policy";
import { indexNodesById } from "../node-tree";
import { resolveProtectionDevices } from "../protection-devices";
import type { Issue, System } from "../types";
import { buildGraph, v, type Edge, type Graph, type Vertex } from "./graph";

/** Rating evidence follows the physical device boundary. Inheriting a holder
 * rating for cable ampacity must not let a branch on its input post bypass the
 * actual fuse. Equal legacy cable declarations may mirror that holder. */
function manufacturerProtection(system: System, lookup: DefLookup) {
  const cableRatings = new Map(system.cables.flatMap(c => c.fuseAmpOverride != null
    ? [[c.id, c.fuseAmpOverride] as const] : []));
  const holders = new Map<string, { ratingA: number; terminals: string[] }>();
  const sources = new Map<Vertex, number>();
  for (const device of resolveProtectionDevices(system, lookup)) {
    if (device.kind !== "holder" && device.kind !== "battery-post") continue;
    for (const id of device.unresolvedCableDeclarations) cableRatings.delete(id);
    if (device.ratingA == null) continue;
    if (device.kind === "holder") {
      holders.set(device.nodeIds[0], { ratingA: device.ratingA, terminals: device.terminals });
    } else {
      for (const vertex of device.terminals) sources.set(vertex, device.ratingA);
    }
  }
  const edgeRating = (edge: Edge): number | undefined => {
    if (edge.kind === "cable") return cableRatings.get(edge.cableId);
    const holder = holders.get(edge.link.nodeId);
    return holder && holder.terminals.includes(v(edge.link.nodeId, edge.link.from)) &&
      holder.terminals.includes(v(edge.link.nodeId, edge.link.to))
      ? holder.ratingA : edge.link.breakerRating ?? edge.link.fuseRating;
  };
  return { cableRatings, holders, sources, edgeRating };
}
type ManufacturerProtection = ReturnType<typeof manufacturerProtection>;

/** Find a battery supply path after opening protection satisfying the supplied
 * rating predicate. A surviving path bypasses that numeric requirement; this
 * never establishes device identity, trip curve or series coordination. */
function supplyFusePath(
  graph: Graph, start: Vertex, batteries: ReadonlySet<Vertex>,
  protects?: (rating: number) => boolean, physical?: ManufacturerProtection,
  initialCableId?: string, preferRatedWitness = false,
) {
  const queue = [start];
  const previous = new Map<Vertex, { from: Vertex; rating?: number; cableId?: string }>();
  const seen = new Set([start]);
  let unrated: { ratings: number[]; cableIds: string[] } | undefined;
  for (let i = 0; i < queue.length; i++) {
    const at = queue[i];
    const sourceRating = physical?.sources.get(at);
    if (batteries.has(at) && !(sourceRating != null && protects?.(sourceRating))) {
      const ratings: number[] = sourceRating != null ? [sourceRating] : [], cableIds: string[] = [];
      let cur = at;
      while (previous.has(cur)) {
        const step = previous.get(cur)!;
        if (step.rating != null) ratings.push(step.rating);
        if (step.cableId != null) cableIds.push(step.cableId);
        cur = step.from;
      }
      if (!preferRatedWitness || ratings.length > 0) return { ratings, cableIds };
      unrated ??= { ratings, cableIds };
    }
    for (const { vertex, via } of graph.adj.get(at) ?? []) {
      if (at === start && initialCableId != null &&
        (via.kind !== "cable" || via.cableId !== initialCableId)) continue;
      const rating = physical ? physical.edgeRating(via) : via.kind === "cable" ? via.fuseRating : via.link.breakerRating ?? via.link.fuseRating;
      if (rating != null && rating > 0 && protects?.(rating)) continue;
      if (seen.has(vertex)) continue;
      seen.add(vertex);
      previous.set(vertex, { from: at, rating, cableId: via.kind === "cable" ? via.cableId : undefined });
      queue.push(vertex);
    }
  }
  return unrated;
}

/** Manufacturer installation minima apply even when generic wire ampacity
 * and voltage drop pass. Report pinned choices; never silently change them. */
export function detectInstallationLimits(system: System, defLookup: DefLookup): Issue[] {
  const issues: Issue[] = [];
  const nodesById = indexNodesById(system.nodes);
  const cablesById = new Map(system.cables.map((c) => [c.id, c]));
  const reportedWire = new Set<string>();
  const checkedFuse = new Set<Vertex>();
  let graph: Graph | undefined;
  let batteries: ReadonlySet<Vertex> | undefined;
  let physicalProtection: ManufacturerProtection | undefined;
  const runs = computeInstallationRuns(system, defLookup);
  const ip43Runs = runs.filter(run => run.verificationReason || run.terminalWireMaximumMm2 != null);
  const ip43Specs = ip43Runs.length ? computeSystemCableSpecs(system, defLookup) : undefined;
  for (const nodeId of new Set(ip43Runs.map(run => run.nodeId))) {
    const leads = ip43Runs.filter(run => run.nodeId === nodeId);
    const reasons = new Set(leads.flatMap(run => run.verificationReason ? [run.verificationReason] : []));
    const largeTerminals = leads.filter(run => run.terminalWireMaximumMm2 != null &&
      (ip43Specs?.get(run.cableIds[0])?.wire.mm2 ?? 0) > run.terminalWireMaximumMm2);
    if (!reasons.size && !largeTerminals.length) continue;
    issues.push({ code: "manufacturer-installation-unverified", severity: "warning",
      title: { key: "diag.manufacturer-installation-unverified.title", vars: { label: leads[0].label } },
      message: { key: "diag.manufacturer-installation-unverified.ip43" },
      fix: { key: "diag.manufacturer-installation-unverified.ip43-fix" },
      details: [...reasons].sort().map(reason => ({ key: `diag.manufacturer-installation-unverified.${reason}` }))
        .concat(largeTerminals.length ? [{ key: "diag.manufacturer-installation-unverified.ip43-terminal" }] : []),
      nodeIds: [nodeId], cableIds: [...new Set([...leads.filter(run => run.verificationReason).flatMap(run => run.cableIds),
        ...largeTerminals.map(run => run.cableIds[0])])],
    });
  }
  const parallelNodes = new Set(runs.filter(run => run.parallelWiringUnverified).map(run => run.nodeId));
  for (const nodeId of parallelNodes) {
    const leads = runs.filter(run => run.nodeId === nodeId && run.parallelWiringUnverified);
    issues.push({ code: "manufacturer-installation-unverified", severity: "warning",
      title: { key: "diag.manufacturer-installation-unverified.title", vars: { label: leads[0].label } },
      message: { key: "diag.manufacturer-installation-unverified.parallel" },
      fix: { key: "diag.manufacturer-installation-unverified.parallel-fix" },
      nodeIds: [nodeId], cableIds: [...new Set(leads.flatMap(run => run.cableIds))],
    });
  }
  for (const run of runs) {
    const cables = run.cableIds.map((id) => cablesById.get(id)!);
    for (const cable of cables) {
      const mm2 = cable.wireSizeMm2Override;
      if (mm2 == null || mm2 >= run.minimumWireMm2 || reportedWire.has(cable.id)) continue;
      reportedWire.add(cable.id);
      issues.push({
        code: "manufacturer-wire-minimum",
        severity: "warning",
        title: { key: "diag.manufacturer-wire-minimum.title", vars: { label: run.label } },
        message: { key: run.minimumWireLabel ? "diag.manufacturer-wire-minimum.ip43" : "diag.manufacturer-wire-minimum.message", vars: {
          label: run.label, mm2, minimum: run.minimumWireMm2, ...(run.minimumWireLabel ? { minimumLabel: run.minimumWireLabel } : {}),
        } },
        fix: { key: "diag.manufacturer-wire-minimum.fix" },
        nodeIds: [run.nodeId], cableIds: [cable.id],
      });
    }
    const maximum = run.suppliedLead?.ampacityA ?? run.fuse?.maxA ?? run.manufacturerBreaker?.ratingA;
    if (maximum == null) continue;
    const start = v(run.nodeId, run.terminalId);
    if (checkedFuse.has(start)) continue;
    checkedFuse.add(start);
    graph ??= buildGraph(system, defLookup, { excludeSense: true });
    batteries ??= bankPositiveVertices(computeBatteryStacks(system, defLookup));
    const physical = run.manufacturerBreaker
      ? physicalProtection ??= manufacturerProtection(system, defLookup) : undefined;
    const supply = supplyFusePath(graph, start, batteries, undefined, physical);
    const protects = (rating: number) => run.manufacturerBreaker
      ? rating === run.manufacturerBreaker.ratingA : rating <= maximum;
    // Inspect each direct supply lead independently. Otherwise an unfused
    // first witness could hide a known incompatible rating on another feed.
    // Keeping the starting vertex visited prevents a dead-end fuse stub from
    // becoming a supposed supply path by walking back through the device post.
    const witnesses = run.manufacturerBreaker
      ? (graph.adj.get(start) ?? []).flatMap(edge => {
        if (edge.via.kind !== "cable") return [];
        const path = supplyFusePath(graph!, start, batteries!, protects, physical, edge.via.cableId, true);
        return path ? [path] : [];
      })
      : [supplyFusePath(graph, start, batteries, protects)].filter(path => path != null);
    const unprotected = witnesses.find(path => path.ratings.length > 0) ?? witnesses[0];
    const directRatings = cables.map((c) => physical
      ? physical.cableRatings.get(c.id) : getEffectiveCableFuse(c, nodesById, defLookup).ratingA)
      .filter((a): a is number => a != null);
    if (physical) {
      const vertices = new Set(cables.flatMap(c => [v(c.fromNodeId, c.fromTerminalId), v(c.toNodeId, c.toTerminalId)]));
      for (const holder of physical.holders.values()) {
        if (holder.terminals.length > 1 && holder.terminals.every(vertex => vertices.has(vertex))) {
          directRatings.push(holder.ratingA);
        }
      }
    }
    if (run.manufacturerBreaker) {
      const required = run.manufacturerBreaker.ratingA;
      // A matching numeric breaker/fuse rating does not establish the required
      // manufacturer's motor trip curve. Keep the device-verification warning.
      issues.push({
        code: "manufacturer-breaker-unverified", severity: "warning",
        title: { key: "diag.manufacturer-breaker-unverified.title", vars: { label: run.label, rating: required } },
        message: { key: "diag.manufacturer-breaker-unverified.message", vars: { label: run.label, rating: required } },
        fix: { key: "diag.manufacturer-breaker-unverified.fix" },
        nodeIds: [run.nodeId], cableIds: run.cableIds,
      });
      // Every battery path must include the required numeric rating. A larger
      // feeder device before a matching branch breaker is not a conflict, but
      // an alternate battery path bypassing that breaker remains one.
      const path = unprotected;
      const ratings = supply ? path?.ratings ?? [] : directRatings;
      if (ratings.length > 0 && !ratings.includes(required)) {
        issues.push({
          code: "manufacturer-breaker-rating", severity: "warning",
          title: { key: "diag.manufacturer-breaker-rating.title", vars: { label: run.label } },
          message: { key: "diag.manufacturer-breaker-rating.message", vars: {
            label: run.label, rating: Math.min(...ratings), required,
          } },
          fix: { key: "diag.manufacturer-breaker-rating.fix" },
          nodeIds: [run.nodeId], cableIds: [...new Set([...run.cableIds, ...(path?.cableIds ?? [])])],
        });
      }
      continue;
    }
    const path = unprotected?.ratings.length ? unprotected : supply;
    const ratings = path?.ratings.length ? path.ratings : directRatings;
    if (ratings.length === 0) continue; // Missing protection has its own detector.
    // Two fuses in series protect at the lower rating: a 100 A main followed
    // by a 20 A branch does not leave a SmartSolar protected at 100 A.
    const rating = Math.min(...ratings);
    if (run.suppliedLead) {
      if (rating <= run.suppliedLead.ampacityA) continue;
      issues.push({
        code: "manufacturer-supplied-lead-protection",
        severity: "warning",
        title: { key: "diag.manufacturer-supplied-lead-protection.title", vars: { label: run.label } },
        message: { key: "diag.manufacturer-supplied-lead-protection.message", vars: {
          label: run.label, rating, mm2: run.suppliedLead.wireMm2, ampacity: run.suppliedLead.ampacityA,
        } },
        fix: { key: "diag.manufacturer-supplied-lead-protection.fix" },
        nodeIds: [run.nodeId], cableIds: [...new Set([...run.cableIds, ...(path?.cableIds ?? [])])],
      });
      continue;
    }
    if (!run.fuse) continue;
    if (rating >= run.fuse.minA && rating <= run.fuse.maxA) continue;
    issues.push({
      code: "manufacturer-fuse-range",
      severity: "warning",
      title: { key: "diag.manufacturer-fuse-range.title", vars: { label: run.label } },
      message: { key: "diag.manufacturer-fuse-range.message", vars: {
        label: run.label, rating, min: run.fuse.minA, max: run.fuse.maxA,
      } },
      fix: { key: "diag.manufacturer-fuse-range.fix" },
      nodeIds: [run.nodeId], cableIds: [...new Set([...run.cableIds, ...(path?.cableIds ?? [])])],
    });
  }
  for (const run of computeFridgeInstallationPolicies(system, defLookup).installations) {
    if (run.verificationReason) issues.push({
      code: "manufacturer-installation-unverified", severity: "warning",
      title: { key: "diag.manufacturer-installation-unverified.title", vars: { label: run.label } },
      message: { key: `diag.manufacturer-installation-unverified.${run.verificationReason}` },
      fix: { key: "diag.manufacturer-installation-unverified.fix" },
      nodeIds: [run.nodeId], cableIds: run.cableIds,
    });
    for (const id of run.cableIds) {
      const cable = cablesById.get(id);
      const mm2 = cable?.wireSizeMm2Override;
      if (mm2 == null || run.minimumWireMm2 == null || mm2 >= run.minimumWireMm2 || reportedWire.has(id)) continue;
      reportedWire.add(id);
      issues.push({ code: "manufacturer-wire-minimum", severity: "warning",
        title: { key: "diag.manufacturer-wire-minimum.title", vars: { label: run.label } },
        message: { key: "diag.manufacturer-wire-minimum.message", vars: { label: run.label, mm2, minimum: run.minimumWireMm2 } },
        fix: { key: "diag.manufacturer-wire-minimum.fix" }, nodeIds: [run.nodeId], cableIds: [id],
      });
    }
    if (run.fuseA == null) continue;
    // Only positive supply paths carry this requirement. A larger upstream
    // feeder fuse neither substitutes for branch protection nor proves a CRX
    // maximum; flag known undersized declarations without inventing one.
    const low = [...new Set([...run.positiveCableIds, ...run.dedicatedPositiveCableIds])].flatMap(id => {
      const cable = cablesById.get(id);
      const rating = cable ? getEffectiveCableFuse(cable, nodesById, defLookup).ratingA : undefined;
      return rating != null && rating < run.fuseA! ? [{ id, rating }] : [];
    });
    if (low.length) issues.push({ code: "manufacturer-fuse-minimum", severity: "warning",
      title: { key: "diag.manufacturer-fuse-minimum.title", vars: { label: run.label } },
      message: { key: "diag.manufacturer-fuse-minimum.message", vars: { label: run.label, rating: Math.min(...low.map(x => x.rating)), minimum: run.fuseA } },
      fix: { key: "diag.manufacturer-fuse-minimum.fix" }, nodeIds: [run.nodeId], cableIds: low.map(x => x.id),
    });
  }
  return issues;
}
